import { datadogRum } from '@datadog/browser-rum';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import {
  BrowseDocument,
  BrowseResponse,
  browseStructure,
  Category,
  Subcategory,
  Subtitle,
} from '@cfa/pathway-fe-shared/xp';

import {
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';
import { LanguageObject } from '@/types';

import { userPrefsAtom } from '@/atoms/userPrefs';

type StructuredDocumentMap = Record<string, BrowseDocument>;
type StructuredSubtitleMap = Record<
  string,
  (Subtitle & { documentMap: StructuredDocumentMap }) | undefined
>;
export type StructuredSubcategoryMap = Record<
  string,
  (Subcategory & { subtitleMap: StructuredSubtitleMap }) | undefined
>;

export type StructuredCategory = Category & {
  subcategoryMap: StructuredSubcategoryMap;
};

// Adding types to the array chidren
export type UnkeyedSubtitle = Omit<Subtitle, 'documents'> & {
  documents?: BrowseDocument[];
};

export type UnkeyedSubcategory = Omit<Subcategory, 'subtitles' | 'name'> & {
  name?: string | LanguageObject;
  subtitles?: UnkeyedSubtitle[] | IntlSubtitleDTO[];
};

export type UnkeyedCategory = Omit<Category, 'subcategories'> & {
  subcategories?: UnkeyedSubcategory[] | IntlSubcategoryDTO[];
};

export type UnkeyedDocument = Omit<BrowseDocument, 'name'> & {
  name?: string | LanguageObject;
};

type WithChildArray<T, K extends string, V> = T & Record<K, V[]>;
type WithChild<T, K extends string, V> = T & Record<K, V>;

// Sanitize subtitles/documents so downstream consumers never see documents without IDs
const sanitizeSubtitlesDocuments = (
  subtitles: Subtitle[] | undefined,
  allDocuments: BrowseDocument[] | undefined,
  categoryId: string,
  subcategoryId: string,
): UnkeyedSubtitle[] => {
  if (!subtitles || !allDocuments) return [];

  return buildArrays<Subtitle, 'documents', BrowseDocument>(
    subtitles.reduce<string[]>(
      (acc, sub) => (sub.id ? [...acc, sub.id] : acc),
      [],
    ),
    subtitles,
    'documents',
    subtitle =>
      buildArrays<BrowseDocument, 'documents', never>(
        subtitle.documents ?? [],
        allDocuments,
      ),
  ).map(subtitle => ({
    ...subtitle,
    documents: subtitle.documents.filter((document: BrowseDocument) => {
      const docId = document.id;
      if (!docId) {
        datadogRum.addError(
          new Error(
            'Structured browse document missing ID (useGetStructuredBrowseData)',
            {
              cause: {
                document,
                categoryId,
                subcategoryId,
                subtitleId: subtitle.id,
              },
            },
          ),
        );
        return false;
      }
      return true;
    }),
  })) as UnkeyedSubtitle[];
};

// Builds each of the arrays with actual objects rather than IDs. One day we may get this from the API and delete this code
const buildArrays = <T extends { id?: string }, K extends string, V>(
  ids: string[] | undefined,
  source: T[] | undefined,
  childKey?: K,
  childBuilder?: (obj: T) => V[],
): WithChildArray<T, K, V>[] => {
  if (!ids || !source) return [];

  return ids
    .map(id => {
      const obj = source.find(item => item.id === id);
      if (!obj) return undefined;

      if (childKey && childBuilder) {
        return { ...obj, [childKey]: childBuilder(obj) } as WithChildArray<
          T,
          K,
          V
        >;
      }

      return { ...obj } as WithChildArray<T, K, V>;
    })
    .filter((x): x is WithChildArray<T, K, V> => Boolean(x));
};

// In case we ever want to return a keyed object for each of the nested arrays for more performant lookups:
const buildMap = <T extends { id?: string }, K extends string, V>(
  ids: string[] | undefined,
  source: T[] | undefined,
  childKey?: K,
  childBuilder?: (obj: T) => V,
): Record<string, WithChild<T, K, V>> => {
  if (!ids || !source) return {};

  return ids.reduce<Record<string, WithChild<T, K, V>>>((acc, id) => {
    const obj = source.find(item => item.id === id);
    if (!obj) return acc;

    if (childKey && childBuilder) {
      acc[id] = {
        ...obj,
        [childKey]: childBuilder(obj),
      } as WithChild<T, K, V>;
    } else {
      acc[id] = obj as WithChild<T, K, V>;
    }

    return acc;
  }, {});
};

export default function useGetStructuredBrowseData(opts: {
  structure?: 'keyed';
  enabled?: boolean;
}): UseQueryResult<StructuredCategory[]>;
export default function useGetStructuredBrowseData(opts: {
  structure?: 'unkeyed';
  enabled?: boolean;
}): UseQueryResult<UnkeyedCategory[]>;
export default function useGetStructuredBrowseData(opts?: {
  structure?: 'default';
  enabled?: boolean;
}): UseQueryResult<BrowseResponse>;

export default function useGetStructuredBrowseData({
  structure = 'default',
  enabled = true,
}: { structure?: 'keyed' | 'unkeyed' | 'default'; enabled?: boolean } = {}) {
  const { country, language } = useAtomValue(userPrefsAtom);

  return useQuery<StructuredCategory[] | UnkeyedCategory[] | BrowseResponse>({
    enabled: !!country.id && !!language && enabled,
    queryKey: ['browse', { structure, country: country.id, language }],
    queryFn: async () => {
      const { data: response } = await browseStructure({
        query: {
          country: country.id,
          language,
        },
      });

      if (structure === 'default') {
        if (!response) {
          throw new Error('response is null');
        }
        return response;
      }

      const {
        categories: allCategories,
        subcategories: allSubcategories,
        subtitles: allSubtitles,
        documents: allDocuments,
      } = response ?? {};

      if (!allCategories) return [];

      let categories: StructuredCategory[] | UnkeyedCategory[] = [];

      if (structure === 'keyed') {
        categories = allCategories.map(cat => {
          const subcategoryMap = buildMap<
            Subcategory,
            'subtitleMap',
            StructuredSubtitleMap
          >(cat.subcategories, allSubcategories, 'subtitleMap', subcat =>
            buildMap<Subtitle, 'documentMap', StructuredDocumentMap>(
              subcat.subtitles,
              allSubtitles,
              'documentMap',
              subtitle =>
                buildMap<BrowseDocument, 'documentMap', StructuredDocumentMap>(
                  subtitle.documents,
                  allDocuments,
                ),
            ),
          );

          return {
            ...cat,
            subcategoryMap,
          };
        });
      }

      if (structure === 'unkeyed') {
        categories = allCategories.map((cat: Category) => {
          const subcategoryArray = buildArrays<
            Subcategory,
            'subtitles',
            UnkeyedSubtitle
          >(
            cat.subcategories ?? [],
            allSubcategories ?? [],
            'subtitles',
            subcat =>
              sanitizeSubtitlesDocuments(
                (subcat.subtitles ?? [])
                  .map(id => (allSubtitles ?? []).find(s => s.id === id))
                  .filter(
                    (subtitle): subtitle is Subtitle => subtitle !== undefined,
                  ),
                allDocuments ?? [],
                cat.id,
                subcat.id,
              ),
          );

          return { ...cat, subcategories: subcategoryArray };
        });
      }

      return categories.sort(
        (cat1, cat2) => cat1.displayIndex - cat2.displayIndex,
      );
    },
    staleTime: Infinity,
  });
}
